import type { Database, Json } from '@/types/database.generated';

type CommunicationServiceChannel = Database['public']['Enums']['communication_service_channel'];
type ConsentRecordMethod = Database['public']['Enums']['consent_record_method'];
type PreferenceCurrentRow = Database['public']['Views']['patient_communication_preference_current']['Row'];

/** patients.sms_consent JSON written before communication_preferences existed. */
export type LegacySmsConsent = Json | null | undefined;

export interface ResolvedCommunicationPreference {
  preferredChannel: CommunicationServiceChannel;
  smsOptOut: boolean;
  emailOptOut: boolean;
  source: 'preference' | 'legacy_sms_consent' | 'default';
  setAt: string | null;
  method: ConsentRecordMethod | null;
}

function legacySmsDeclined(value: LegacySmsConsent): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const status = (value as Record<string, Json>).status;
  return status === 'declined' || status === 'revoked';
}

/**
 * Service messages are not consent: SMS and e-mail are allowed unless the
 * patient opted out. With no preference row, a legacy declined / revoked SMS
 * consent still counts as an SMS opt-out.
 */
export function resolveCommunicationPreference(
  row: PreferenceCurrentRow | null,
  legacySmsConsent: LegacySmsConsent,
): ResolvedCommunicationPreference {
  if (row?.preferred_channel) {
    return {
      preferredChannel: row.preferred_channel,
      smsOptOut: row.sms_opt_out === true,
      emailOptOut: row.email_opt_out === true,
      source: 'preference',
      setAt: row.set_at ?? null,
      method: row.method ?? null,
    };
  }
  const legacyDeclined = legacySmsDeclined(legacySmsConsent);
  return {
    preferredChannel: 'sms',
    smsOptOut: legacyDeclined,
    emailOptOut: false,
    source: legacyDeclined ? 'legacy_sms_consent' : 'default',
    setAt: null,
    method: null,
  };
}
